/**
 * App-side wiring for the client telemetry census.
 *
 * The state and the serialiser live in `@tendly/shared`; what lives HERE is the
 * only part that is genuinely app-specific: reading `import.meta.env` (a Vite
 * `define`, so it does not exist in the shared package) and deciding which
 * platform this copy is running on.
 *
 * Importing this module IS the initialisation — `lib/api.ts` imports it to build
 * the API client, so there is no separate "remember to call init at boot" step
 * to forget. That mattered more than elegance: the failure mode this whole
 * feature guards against is precisely a census that is wired everywhere except
 * the one place that fills it in.
 */

import { buildClientInfoHeader, type ClientPlatform, setClientBuildIdentity } from "@tendly/shared";

import { isTauriRuntime } from "@/lib/platform";

/**
 * Which distribution channel this copy runs in.
 *
 * The distinction that matters for the census: a stale PWA is fixed by a Pages
 * deploy plus a Service Worker swap, a stale APK needs a store release the user
 * has to accept. Same number, different remediation, different timescale.
 *
 * Note the trap `platform.ts` already documents for purchases and which applies
 * verbatim here: Chrome on an Android phone is `web`, not `android`. Only the
 * native WebView build is `android`.
 */
function detectClientPlatform(): ClientPlatform {
  if (!isTauriRuntime()) {
    return "web";
  }
  const ua = globalThis.navigator?.userAgent?.toLowerCase() ?? "";
  if (ua.includes("android")) {
    return "android";
  }
  if (ua.includes("iphone") || ua.includes("ipad")) {
    return "ios";
  }
  return "desktop";
}

setClientBuildIdentity({
  // Both are Vite `define` substitutions from `apps/web/vite.config.ts`, which
  // sources the version from the repo-root `VERSION` file and the hash from
  // `COMMIT_HASH`/`GIT_HASH`/`git rev-parse`. Same two values `build.rs` bakes
  // into the server, so a mismatch in the panel is a real mismatch.
  appVersion: import.meta.env.APP_VERSION ?? null,
  buildHash: import.meta.env.COMMIT_HASH ?? null,
  platform: detectClientPlatform(),
  // REAL number here, not null: this repo DOES have a root `api-contract.json`
  // (read by `build.rs` and by `vite.config.ts`), so the census can report the
  // one field a `min_supported` decision actually rests on
  // (infra/API_COMPAT_POLICY.md). The canon reports `null` only because listto
  // has no contract file; reporting a literal would be worse than nothing, and
  // reporting the baked value is the whole point of the census.
  contractVersion: import.meta.env.API_CONTRACT_VERSION ?? null,
});

export { buildClientInfoHeader };
